import React, { useState, useEffect } from 'react';
import { Layout } from 'antd';
import { useQuery } from '@common/hooks';
import { Link, Icon } from '@common/components';
import { clsx } from 'clsx';
import { Colors, FontSize } from '@common/tokens';
import { Spacing } from 'works/legacy/lib/style-helper';
import { MessengerLauncherWrapper } from 'works/legacy/components/messenger-launcher-wrapper';
import { AppHeaderQuery, AppHeaderQueryResponse } from '@common/graphql';
// NOTE: imported by file, not through the domain barrels, because those
// barrels import @common/components and would cycle back to this file.
import { AppHeaderPendingApprovalItems } from '@institutions/components/app-header-pending-approval-items';
import { SystemMessageBannerContainer } from '@common/components/system-message-banner';
import { AppSearch } from '@institutions/components/app-search';
import { AppHeaderAccountsMenu } from '@identity/components/app-header-accounts-menu';

const { Header } = Layout;

// TODO(WORK-5878): Extract header height into a shared constant once more consumers depend on it.
const HEADER_BASE_HEIGHT = 72;
const NOTIFICATION_BAR_HEIGHT = 42;

const updateHeaderHeightCssVar = (notificationBarCount: number) => {
  const totalHeight =
    HEADER_BASE_HEIGHT + notificationBarCount * NOTIFICATION_BAR_HEIGHT;
  document.documentElement.style.setProperty(
    '--wk-header-height',
    `${totalHeight}px`
  );
};

export const AppHeader = () => {
  const { data } = useQuery<AppHeaderQueryResponse>(AppHeaderQuery);
  const breadcrumbs = data?.header?.breadcrumbs || [];
  const [hasNotificationOffset, setHasNotificationOffset] = useState(false);
  const [hasBanner, setHasBanner] = useState(false);

  const notificationBarCount =
    (hasNotificationOffset ? 1 : 0) + (hasBanner ? 1 : 0);

  useEffect(() => {
    updateHeaderHeightCssVar(notificationBarCount);
  }, [notificationBarCount]);

  return (
    <>
      <div
        className={clsx(
          'AppHeader',
          notificationBarCount >= 1 && 'AppHeader--notification-offset',
          notificationBarCount >= 2 && 'AppHeader--double-notification'
        )}
      >
        <Header>
          <div className="header-container">
            <div className="header-title">
              {breadcrumbs.map((crumb) => (
                <Link
                  className={clsx('crumb-link', {
                    'sentry-mask': crumb.privateInfo,
                  })}
                  key={crumb.name}
                  to={crumb.path}
                >
                  {crumb.name}
                </Link>
              ))}
            </div>
            <div className="header-search">
              <div className="search">
                <AppSearch />
              </div>
            </div>
            <div className="intercom">
              <MessengerLauncherWrapper>
                <div className="intercom-avatar">
                  <Icon icon="commentAltSmile" kind="light" size="lg" />
                </div>
              </MessengerLauncherWrapper>
            </div>
            <AppHeaderAccountsMenu />
          </div>
        </Header>
        <SystemMessageBannerContainer onVisibilityChange={setHasBanner} />
        <AppHeaderPendingApprovalItems
          onVisibilityChange={setHasNotificationOffset}
        />
      </div>
      <style jsx>{`
        .AppHeader {
          box-shadow: 0 0 12px rgba(25, 26, 26, 0.08);
          z-index: 2;
          position: fixed;
          width: 100vw;
          padding-left: 80px;
        }
        .AppHeader :global(.ant-layout-header) {
          z-index: 1;
          background: #fff;
          padding: 0;
          min-height: 72px;
          line-height: 72px;
          min-width: 320px;
          height: auto;
        }
        .AppHeader .header-container {
          display: flex;
        }
        .AppHeader .header-title {
          display: flex;
          flex-grow: 1;
          min-width: 180px;
          overflow: hidden;
          padding: 0 ${Spacing.baseSpacing / 2}px 0 ${Spacing.baseSpacingPx};
        }
        .AppHeader .header-search {
          flex-grow: 0.2;
        }
        .AppHeader .search {
          display: flex;
          min-width: 120px;
          margin-right: 8px;
          height: 100%;
          align-items: center;
        }
        .AppHeader .intercom {
          min-width: 46px;
        }
        .AppHeader .intercom-avatar {
          background: white;
          width: 40px;
          height: 40px;
          margin-top: 16px;
          border-radius: 50%;
          position: relative;
        }
        .AppHeader .intercom-avatar :global(.wk-icon) {
          position: absolute;
          top: 12px;
          left: 10px;
        }
        .AppHeader :global(.intercom-icon) {
          margin-bottom: ${Spacing.baseSpacing / 2}px;
          margin-left: 10px;
          font-size: ${FontSize.xl};
        }
        .AppHeader .intercom-avatar:hover {
          background: ${Colors.elmLightest};
        }
        .AppHeader :global(.crumb-link) {
          color: ${Colors.midnightLighter};
          font-size: ${FontSize.xl};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .AppHeader :global(.crumb-link:hover) {
          color: ${Colors.elm};
        }
        .AppHeader :global(.crumb-link:not(:first-child):before) {
          display: inline-block;
          content: '/';
          pointer-events: none;
          color: rgba(0, 0, 0, 0.3);
          margin: 0 ${Spacing.baseSpacing / 3}px;
        }
        .AppHeader :global(.crumb-link:last-child) {
          color: ${Colors.midnightDarkest};
        }
      `}</style>
    </>
  );
};
